import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import InputComponent from 'app_components/commons/Input/InputComponent'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import CertificateSettingsService from 'app_services/admin/certificateSettings/certificateSettingsService'
import DurationFieldService from 'app_services/components/durationFieldService'
import { CertificateSettingType, CERTIFICATION_SETTINGS_BY_MODALITY, ICertificateSettingItemConfiguration, ICertificateSettings, ICertificateSettingsModule } from 'app_types/admin/certificateSettings/certificateSettings.types'
import { ICourseSchedulingDetailListItem } from 'app_types/admin/courseScheduling/courseSchedulingDetailsTypes'
import { CourseSchedulingModes } from 'app_types/admin/courseScheduling/courseSchedulingModeTypes'
import { ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import CertificateSettingsModuleForm from '../CertificateSettingsModuleForm/CertificateSettingsModuleForm'

import './CertificateSettingsForm.scss'

interface ICertificateSettingsFormProps {
  editData?: ICertificateSettings
  courseSchedulingDetails: ICourseSchedulingDetailListItem[]
  courseScheduling: ICourseSchedulingListItem
  onCancel?: () => void
  onSaved?: () => void
}

const CertificateSettingsForm: FC<ICertificateSettingsFormProps> = ({
  editData,
  courseSchedulingDetails,
  courseScheduling,
  onCancel,
  onSaved,
}) => {
  const formHook = useForm({ defaultValues: {
    ...editData,
    certificationType: editData?.certificationType ? editData?.certificationType : CertificateSettingType.ATTENDANCE
  } })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { courseSchedulingId } = useParams<{ courseSchedulingId: string }>()
  const screenNotification = useMemo(() => new ScreenNotificationsUtil(), [])
  const certificateSettingsService = useMemo(() => new CertificateSettingsService(), [])
  const durationFieldService = useMemo(() => new DurationFieldService(), [])
  const modules = formHook.watch('modules')
  const certificationType = formHook.watch('certificationType')
  const duration = useMemo(() => {
    if (modules?.length) {
      return modules?.map(({ duration }) => duration)
              ?.reduce((sum, item) => sum + (item ? item : 0), 0)
    }
    return 0
  }, [modules])
  const isSaveEnabled = useMemo(() => {
    if (!modules?.length) return false
    return !modules.some((module) =>
      !module.attendance?.status &&
      !module.averageScore?.status &&
      !module.exam?.status &&
      !module.progress?.status
    )
  }, [modules])

  const handleChangeModules = (modules: ICertificateSettingsModule[]) => {
    formHook.setValue('modules', modules)
  }

  const handleFormSubmit = useCallback(async (data: ICertificateSettings) => {
    try {
      if (!data?.modules?.length) return
      data.modules = data.modules.map((module) => ({
        ...module,
        // @ts-ignore
        courseSchedulingDetail: module.courseSchedulingDetail?.value,
      }))
      data.duration = duration
      data.courseScheduling = courseSchedulingId
      setIsLoading(true)
      let response = null
      if (editData?._id) {
        response = await certificateSettingsService.update({ ...data, id: editData?._id })
      } else {
        response = await certificateSettingsService.create({...data})
      }
      if (response?.code === 200) {
        screenNotification.showSuccessTimer()
        onSaved && onSaved()
      }
      setIsLoading(false)
    } catch(e) {
      console.error('Error: ', e)
      setIsLoading(false)
    }
  }, [
    certificateSettingsService,
    editData,
    screenNotification,
    onSaved,
    courseSchedulingId,
    duration,
  ])

  useEffect(() => {
    formHook.register('modules')
  }, [])

  const getDefaultModules = useCallback(() => {
    if (editData?.modules?.length) {
      const defaultModules = editData?.modules?.map((module) => ({
        ...module,
        courseSchedulingDetail: {
          // @ts-ignore
          value: module.courseSchedulingDetail?._id,
          // @ts-ignore
          label: courseSchedulingDetails?.find((item) => item._id === module.courseSchedulingDetail?._id)?.course?.name,
        }
      }))
      return defaultModules
    }
    return []
  }, [editData, courseSchedulingDetails])

  const modulesConfiguration = useMemo(() => {
    const modality = courseScheduling?.schedulingMode?.name as CourseSchedulingModes
    return CERTIFICATION_SETTINGS_BY_MODALITY[modality]?.[certificationType]
  }, [courseScheduling?.schedulingMode?.name, certificationType])

  return (
    <div className="certificate-settings-form--layout">
      <form onSubmit={formHook.handleSubmit(handleFormSubmit)}>
        <div>
          <TextComponent color="black" fontSize="1.2rem">
            <strong>Modalidad:</strong> {courseScheduling?.schedulingMode?.name}
          </TextComponent>
          <TextComponent color="black" fontSize="1.2rem">
            <strong>Duración:</strong> {durationFieldService.getDurationFormated(!!duration ? duration : 0)}
          </TextComponent>
        </div>
        <InputComponent
          placeholder='Nombre del certificado'
          label='Nombre del certificado'
          formHook={formHook.register({ required: true })}
          name='certificateName'
          invalid={!!formHook?.errors?.certificateName}
          invalidText="El nombre del certificado es requerido"
        />
        <div>
          <LabelComponent>
            Tipo de certificado
          </LabelComponent>
          <Controller
              control={formHook.control}
              name='certificationType'
              render={({ ref, value, onChange }) => (
                <select
                  id='certificationType'
                  className='form-control'
                  value={value}
                  ref={ref}
                  onChange={(e) => onChange(e.target.value)}
                >
                  <option value={CertificateSettingType.ATTENDANCE}>
                    Asistencia
                  </option>
                  <option value={CertificateSettingType.ATTENDANCE_APPROVAL}>
                    Asistencia y aprobación
                  </option>
                </select>
              )}
            />
        </div>
        <TextComponent color="black" fontWeight={600}>
          Módulos
        </TextComponent>
        <CertificateSettingsModuleForm
          courseSchedulingDetails={courseSchedulingDetails}
          onChange={handleChangeModules}
          defaultValue={getDefaultModules() as unknown as ICertificateSettingsModule[]}
          config={modulesConfiguration as ICertificateSettingItemConfiguration}
          programMoodleId={courseScheduling?.moodle_id}
          certificationType={certificationType}
        />
        <div className='certificate-settings-form--actions'>
          <ButtonComponent onClick={onCancel} type='button'>
            Cancelar
          </ButtonComponent>
          <ButtonComponent disabled={!isSaveEnabled} loading={isLoading} variant='primary'>
            {editData?._id ? 'Guardar' : 'Crear'}
          </ButtonComponent>
        </div>
      </form>
    </div>
  )
}

export default CertificateSettingsForm
